import { useCallback, useMemo, useSyncExternalStore } from "react";

import { buildCleanLabelMap, displayName } from "./wbsCode";

/**
 * App-wide "show WBS codes" preference.
 *
 * Every PMS screen that renders phase/activity names reads this, and the tag
 * button in each screen's toolbar writes it — so flipping it on the Tasks page
 * also changes the Phases list, the Gantt, the drawers and so on. Kept in a
 * tiny external store rather than Redux because nothing else needs to react to
 * it, and in localStorage so it survives reloads.
 *
 * OFF (default) = trimmed label: code and area prefix removed.
 * ON             = the raw stored name, exactly as the DB holds it.
 */
const STORAGE_KEY = "pms.showWbsCodes";

const read = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    // Private mode / storage disabled — fall back to the default.
    return false;
  }
};

let current = read();
const listeners = new Set<() => void>();

const getSnapshot = () => current;

const subscribe = (onChange: () => void) => {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
};

export const setShowWbsCodes = (next: boolean) => {
  if (next === current) return;
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
  } catch {
    // Preference just won't persist; the in-memory value still drives the UI.
  }
  listeners.forEach((notify) => notify());
};

// Keep other tabs of the same app in step.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== STORAGE_KEY) return;
    current = read();
    listeners.forEach((notify) => notify());
  });
}

/** `[showWbsCodes, setShowWbsCodes]`, shared across every screen. */
export const useShowWbsCodes = (): [boolean, (next: boolean) => void] => [
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot),
  setShowWbsCodes,
];

/**
 * Formatter for a standalone phase/activity name — drawer titles, breadcrumbs,
 * dropdown options, chart labels, anywhere without sibling context.
 */
export const useWbsName = (): ((raw?: string | null) => string) => {
  const [showWbsCodes] = useShowWbsCodes();
  return useCallback(
    (raw?: string | null) => (showWbsCodes ? raw ?? "" : displayName(raw)),
    [showWbsCodes]
  );
};

/**
 * Formatter for names rendered inside a tree, where the area prefix is only
 * dropped if the row stays distinguishable from its siblings. Pass the same
 * node array the table renders.
 */
export const useWbsTreeName = (
  nodes?: any[]
): ((key?: string, raw?: string | null) => string) => {
  const [showWbsCodes] = useShowWbsCodes();
  const labels = useMemo(() => buildCleanLabelMap(nodes), [nodes]);
  return useCallback(
    (key?: string, raw?: string | null) => {
      if (showWbsCodes) return raw ?? "";
      return (key && labels.get(key)) || displayName(raw);
    },
    [showWbsCodes, labels]
  );
};

/**
 * Same clash-awareness for a flat list of records — a phase list, an activity
 * list, a set of dropdown options. Give it the array plus which fields hold the
 * id and the name; call the result with a record.
 *
 *   const phaseLabel = useWbsRecordName(phases, "phaseId", "phaseName");
 *   ...
 *   {phaseLabel(record)}
 */
export const useWbsRecordName = (
  records?: any[],
  idField = "phaseId",
  nameField = "phaseName"
): ((record: any) => string) => {
  const nodes = useMemo(
    () =>
      (records || []).map((record: any) => ({
        key: `${idField}:${record?.[idField]}`,
        name: record?.[nameField],
      })),
    [records, idField, nameField]
  );
  const treeName = useWbsTreeName(nodes);

  return useCallback(
    (record: any) =>
      treeName(`${idField}:${record?.[idField]}`, record?.[nameField]),
    [treeName, idField, nameField]
  );
};
